import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  inject,
  OnDestroy,
  signal,
  TemplateRef,
  viewChild,
  ViewContainerRef,
} from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ICellRendererAngularComp } from 'ag-grid-angular';
import { ICellRendererParams } from 'ag-grid-community';
import { TooltipComponent } from './../tooltip/tooltip.component';

export interface WarningCellRendererParams extends ICellRendererParams {
  warningField?: string;
  warningTextField?: string;
  defaultMessage?: string;
}

@Component({
  selector: 'lib-warning-cell-renderer',
  standalone: true,
  imports: [
    CommonModule,
    MatIconModule,
    MatMenuModule,
    TooltipComponent,
    MatTooltipModule,
  ],
  templateUrl: './warning-cell-renderer.component.html',
  styleUrls: ['./warning-cell-renderer.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WarningCellRendererComponent
  implements ICellRendererAngularComp, OnDestroy
{
  params = signal<WarningCellRendererParams | undefined>(undefined);
  showWarning = signal(false);
  tooltipText = signal('');
  tooltipTemplate = viewChild.required<TemplateRef<unknown>>('tooltipTemplate');

  private overlayRef: OverlayRef | null = null;
  private readonly overlay = inject(Overlay);
  private readonly viewContainerRef = inject(ViewContainerRef);

  agInit(params: WarningCellRendererParams): void {
    this.params.set(params);
    this.setValues(params);
  }

  refresh(params: WarningCellRendererParams): boolean {
    this.hideTooltip();
    this.params.set(params);
    this.setValues(params);
    return true;
  }

  private setValues(params: WarningCellRendererParams): void {
    const rendererParams = params.colDef?.cellRendererParams || {};

    const {
      warningField = 'hasWarning',
      warningTextField = 'warningMessage',
      defaultMessage = 'Warning',
    } = rendererParams;

    const rowData = params.data || {};

    this.showWarning.set(Boolean(rowData[warningField]));
    this.tooltipText.set(rowData[warningTextField] || defaultMessage);
  }

  showTooltip(): void {
    const cellElement = this.params()?.eGridCell;

    if (this.overlayRef || !cellElement || !this.showWarning()) {
      return;
    }

    const positionStrategy = this.overlay
      .position()
      .flexibleConnectedTo(cellElement)
      .withPositions([
        {
          originX: 'end',
          originY: 'bottom',
          overlayX: 'end',
          overlayY: 'top',
          offsetY: 0,
        },
        // Fallback Position: Above the cell, aligned right
        {
          originX: 'end',
          originY: 'top', // Target the top edge of the cell
          overlayX: 'end',
          overlayY: 'bottom', // Place the bottom edge of the tooltip there
          offsetY: 0,
        },
      ]);

    this.overlayRef = this.overlay.create({
      positionStrategy,
      panelClass: 'warning-tooltip-overlay',
      scrollStrategy: this.overlay.scrollStrategies.reposition(),
    });

    const portal = new TemplatePortal(
      this.tooltipTemplate(),
      this.viewContainerRef
    );

    this.overlayRef.attach(portal);
  }

  hideTooltip(): void {
    if (this.overlayRef) {
      this.overlayRef.dispose();
      this.overlayRef = null;
    }
  }

  ngOnDestroy(): void {
    this.hideTooltip();
  }
}
